import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { loginUrl } from "@/api";
import { useHost } from "@/host/host";
import { useHostEvents } from "@/host/events";
import { syncHostSession } from "@/host/session";

/**
 * The way in. In the browser, a link to the Discord login that comes back
 * to `next`. Under the companion's shell (decision 064), a button that opens
 * the login in the pilot's browser; the `sharedairframe://auth` link the
 * browser ends on completes it by itself, or is pasted here when it did not.
 */
export function SignIn({ next, className = "btn btn-primary", testId = "login" }: { next: string; className?: string; testId?: string }) {
  const host = useHost();
  if (!host) {
    return (
      <a className={className} href={loginUrl(next)} data-testid={testId}>
        Sign in with Discord
      </a>
    );
  }
  return <HostSignIn className={className} testId={testId} />;
}

function HostSignIn({ className, testId }: { className: string; testId: string }) {
  const host = useHost()!;
  const events = useHostEvents();
  const queryClient = useQueryClient();
  const [pasted, setPasted] = useState("");
  const [error, setError] = useState<string | null>(null);
  const start = useMutation({
    mutationFn: () => host.loginStart(),
    onMutate: () => setError(null),
    onError: (e) => setError(String(e)),
  });
  const complete = useMutation({
    mutationFn: async (input: string) => {
      await host.loginComplete(input);
      await syncHostSession(host);
    },
    onSuccess: () => {
      setPasted("");
      setError(null);
      queryClient.invalidateQueries();
    },
    onError: (e) => setError(String(e)),
  });
  const shown = error ?? events.loginError;
  return (
    <span className="flex flex-wrap items-center gap-2">
      <button type="button" className={className} onClick={() => start.mutate()} disabled={start.isPending} data-testid={testId}>
        Sign in with Discord
      </button>
      {start.isSuccess ? (
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            complete.mutate(pasted);
          }}
        >
          <input className="input" aria-label="Link the browser ended on" placeholder="paste the sharedairframe://auth link if it did not come back by itself" value={pasted} onChange={(e) => setPasted(e.target.value)} />
          <button className="btn" type="submit" disabled={!pasted || complete.isPending}>
            Finish
          </button>
        </form>
      ) : null}
      {shown ? (
        <span className="text-bad text-xs" role="alert">
          {shown}
        </span>
      ) : null}
    </span>
  );
}
